import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import { CUSTOMER_CONTROL_TYPES, RISK_COLOR, STATUS_COLOR } from './constants'

// The DCB0160 support pack: the hazards a deploying organisation needs to
// know about, written for their clinical safety officer. Same chrome as the
// internal hazard-log PDF but a deliberately narrower field set: no causes,
// no notes or file evidence, no acceptability decisions, no review history.
// What goes in is decided per hazard by the CSO (customerFacing.include).

const NAVY = '#123F4A'
const TEAL = '#239AA4'
const MUTED = '#64748B'
const LINE = '#E2E8F0'
const INK = '#334155'

const CONTROL_TYPE_COLOR = {
  Configuration: '#268EBB',
  Training: '#5B48B0',
  Procedure: '#B54708',
  'Business continuity': '#B42318',
  'Awareness only': '#64748B',
}

const clean = (value) =>
  String(value ?? '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/≠/g, '!=')
    .replace(/[—–]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-')
    .replace(/[^\x00-\xFF]/g, '')

const idNumber = (hazardID = '') => Number(String(hazardID).replace(/\D/g, '')) || 0

const riskDetail = (risk) => [risk?.likelihood, risk?.severity].filter(Boolean).join(' / ') || ''

const countBy = (rows, getKey, keys) => {
  const counts = Object.fromEntries(keys.map((key) => [key, 0]))
  let other = 0
  rows.forEach((row) => {
    const key = getKey(row)
    if (key in counts) counts[key] += 1
    else other += 1
  })
  return { counts, other }
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const Pill = ({ label, color }) => (
  <View
    style={{
      backgroundColor: color || '#F1F5F9',
      borderRadius: 3,
      paddingVertical: 1.5,
      paddingHorizontal: 4,
      alignSelf: 'flex-start',
      marginRight: 3,
      marginBottom: 2,
    }}
  >
    <Text style={{ fontSize: 6.5, color: color ? '#FFFFFF' : MUTED, fontFamily: 'Helvetica-Bold' }}>
      {clean(label)}
    </Text>
  </View>
)

const SectionTitle = ({ children, marginTop = 14 }) => (
  <Text
    style={{ fontSize: 11, color: NAVY, fontFamily: 'Helvetica-Bold', marginTop, marginBottom: 4 }}
    minPresenceAhead={40}
  >
    {children}
  </Text>
)

const StatBox = ({ label, value, color }) => (
  <View
    style={{
      flex: 1,
      borderWidth: 0.5,
      borderColor: LINE,
      borderRadius: 4,
      padding: 8,
      marginRight: 6,
    }}
  >
    <Text style={{ fontSize: 6.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Text>
    <Text
      style={{ fontSize: 18, color: color || NAVY, fontFamily: 'Helvetica-Bold', marginTop: 2 }}
    >
      {value}
    </Text>
  </View>
)

const CountTable = ({ title, rows }) => (
  <View style={{ flex: 1, marginRight: 8 }}>
    <Text style={{ fontSize: 8, color: NAVY, fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
      {title}
    </Text>
    {rows.map(([label, value, color]) => (
      <View
        key={label}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 2,
          borderBottomWidth: 0.5,
          borderBottomColor: LINE,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {color ? (
            <View
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                backgroundColor: color,
                marginRight: 4,
              }}
            />
          ) : null}
          <Text style={{ fontSize: 7.5 }}>{label}</Text>
        </View>
        <Text style={{ fontSize: 7.5, fontFamily: 'Helvetica-Bold' }}>{value}</Text>
      </View>
    ))}
  </View>
)

const Label = ({ children }) => (
  <Text
    style={{
      fontSize: 6,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 5,
      marginBottom: 1,
      fontFamily: 'Helvetica-Bold',
    }}
  >
    {children}
  </Text>
)

const Body = ({ children, muted }) => (
  <Text style={{ fontSize: 6.5, color: muted ? MUTED : INK, lineHeight: 1.3 }}>{children}</Text>
)

const Footer = () => (
  <View
    style={{
      position: 'absolute',
      bottom: 16,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
    }}
    fixed
  >
    <Text style={{ fontSize: 6.5, color: '#94A3B8' }}>
      AmbuNet - Customer hazard pack - DCB0160 support - issued to deploying organisations
    </Text>
    <Text
      style={{ fontSize: 6.5, color: '#94A3B8' }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
  </View>
)

const PAGE_STYLE = {
  paddingTop: 30,
  paddingBottom: 36,
  paddingHorizontal: 30,
  fontFamily: 'Helvetica',
  fontSize: 8,
  color: '#0F172A',
}

// ---------------------------------------------------------------------------
// Register table
// ---------------------------------------------------------------------------

const COLUMNS = [
  ['ID', 0.8],
  ['Hazard', 3.4],
  ['Area', 1.4],
  ['Residual risk', 1.1],
  ['Manufacturer controls', 1.2],
  ['What you need to do', 2.2],
]

const TableHeader = () => (
  <View
    style={{
      flexDirection: 'row',
      backgroundColor: NAVY,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 3,
    }}
    fixed
  >
    {COLUMNS.map(([label, flex]) => (
      <Text
        key={label}
        style={{
          flex,
          paddingHorizontal: 3,
          fontSize: 6.5,
          color: '#FFFFFF',
          fontFamily: 'Helvetica-Bold',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          textAlign: label === 'Residual risk' ? 'center' : 'left',
        }}
      >
        {label}
      </Text>
    ))}
  </View>
)

const RegisterRow = ({ hazard, striped }) => {
  const controls = hazard.controlMeasures || []
  const implemented = controls.filter((c) => c?.implemented).length
  const planned = controls.filter((c) => c?.type === 'planned' && !c?.implemented).length
  const rating = hazard.residualRisk?.riskRating
  const types = hazard.customerFacing?.controlTypes || []
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 4,
        backgroundColor: striped ? '#F8FAFC' : undefined,
        borderBottomWidth: 0.5,
        borderBottomColor: LINE,
      }}
      wrap={false}
    >
      <Text style={{ flex: 0.8, paddingHorizontal: 3, fontSize: 7, fontFamily: 'Helvetica-Bold' }}>
        {hazard.hazardID}
      </Text>
      <Text style={{ flex: 3.4, paddingHorizontal: 3, fontSize: 7 }}>{clean(hazard.title)}</Text>
      <Text style={{ flex: 1.4, paddingHorizontal: 3, fontSize: 6.5 }}>{clean(hazard.module)}</Text>
      <View style={{ flex: 1.1, paddingHorizontal: 3, alignItems: 'center' }}>
        <Pill label={rating || 'Not scored'} color={RISK_COLOR[rating]} />
        {riskDetail(hazard.residualRisk) ? (
          <Text style={{ fontSize: 5.5, color: MUTED, textAlign: 'center' }}>
            {riskDetail(hazard.residualRisk)}
          </Text>
        ) : null}
      </View>
      <View style={{ flex: 1.2, paddingHorizontal: 3 }}>
        <Text style={{ fontSize: 7 }}>
          {controls.length ? `${implemented} of ${controls.length} in place` : 'None'}
        </Text>
        {planned > 0 ? (
          <Text style={{ fontSize: 6, color: '#B54708' }}>{planned} planned</Text>
        ) : null}
      </View>
      <View style={{ flex: 2.2, paddingHorizontal: 3, flexDirection: 'row', flexWrap: 'wrap' }}>
        {types.length ? (
          types.map((type) => <Pill key={type} label={type} color={CONTROL_TYPE_COLOR[type]} />)
        ) : (
          <Text style={{ fontSize: 6.5, color: MUTED }}>See guidance</Text>
        )}
      </View>
    </View>
  )
}

// ---------------------------------------------------------------------------
// Detail block
// ---------------------------------------------------------------------------

const HazardDetail = ({ hazard }) => {
  const controls = hazard.controlMeasures || []
  const cf = hazard.customerFacing || {}
  const rating = hazard.residualRisk?.riskRating
  return (
    <View style={{ borderWidth: 0.5, borderColor: LINE, borderRadius: 4, marginBottom: 8 }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#F1F5F9',
          paddingVertical: 4,
          paddingHorizontal: 8,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
        wrap={false}
        minPresenceAhead={80}
      >
        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: NAVY, marginRight: 8 }}>
          {hazard.hazardID}
        </Text>
        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1 }}>
          {clean(hazard.title)}
        </Text>
        <View style={{ marginLeft: 6 }}>
          <Pill label={hazard.status || 'Open'} color={STATUS_COLOR[hazard.status]} />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill label={`Residual risk: ${rating || 'not scored'}`} color={RISK_COLOR[rating]} />
        </View>
      </View>

      <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6 }}>
        <View style={{ flex: 1.3, paddingRight: 10 }}>
          <Text style={{ fontSize: 6.5, color: MUTED, marginBottom: 3 }}>
            {[hazard.module, hazard.hazardType].filter(Boolean).map(clean).join('  |  ')}
          </Text>
          <Text style={{ fontSize: 7, lineHeight: 1.35 }}>
            {clean(cf.summary || hazard.description)}
          </Text>
          <Label>Possible impact</Label>
          {(hazard.clinicalImpact || []).length ? (
            hazard.clinicalImpact.map((item, index) => (
              <Text key={index} style={{ fontSize: 6.5, marginLeft: 6, marginBottom: 1 }}>
                - {clean(item)}
              </Text>
            ))
          ) : (
            <Body muted>Not stated</Body>
          )}
          <Label>Controls in the product</Label>
          {controls.length ? (
            controls.map((control, index) => (
              <View key={index} style={{ flexDirection: 'row', marginBottom: 2 }}>
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    marginTop: 1.5,
                    marginRight: 4,
                    backgroundColor: control.implemented ? '#27803B' : '#B54708',
                  }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 6.5, fontFamily: 'Helvetica-Bold' }}>
                    {clean(control.control || 'Control')}{' '}
                    <Text style={{ fontFamily: 'Helvetica', color: MUTED }}>
                      ({control.implemented ? 'in place' : 'planned, not yet released'})
                    </Text>
                  </Text>
                  {control.description ? <Body>{clean(control.description)}</Body> : null}
                </View>
              </View>
            ))
          ) : (
            <Body muted>None recorded</Body>
          )}
        </View>

        <View
          style={{
            flex: 1,
            borderLeftWidth: 0.5,
            borderLeftColor: LINE,
            paddingLeft: 10,
            backgroundColor: '#F8FAFC',
            paddingVertical: 4,
            paddingRight: 6,
            borderRadius: 3,
          }}
        >
          <Label>What your organisation needs to do</Label>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 3 }}>
            {(cf.controlTypes || []).map((type) => (
              <Pill key={type} label={type} color={CONTROL_TYPE_COLOR[type]} />
            ))}
          </View>
          {cf.guidance ? (
            <Text style={{ fontSize: 7, lineHeight: 1.4, color: INK }}>{clean(cf.guidance)}</Text>
          ) : (
            <Body muted>
              No specific action beyond awareness. Consider whether your own hazard log should
              record this.
            </Body>
          )}
          <Label>Residual risk</Label>
          <Body>
            {rating
              ? `${rating}${riskDetail(hazard.residualRisk) ? ` (${riskDetail(hazard.residualRisk)})` : ''}`
              : 'Not yet scored'}{' '}
            with the product controls above in place.
          </Body>
        </View>
      </View>
    </View>
  )
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

const CustomerHazardPackPDF = ({ hazards = [], downloadedBy }) => {
  const now = moment()
  const sorted = [...hazards].sort((a, b) => idNumber(a.hazardID) - idNumber(b.hazardID))
  const residual = countBy(sorted, (h) => h.residualRisk?.riskRating, [
    'Critical',
    'High',
    'Medium',
    'Low',
  ])
  const typeCounts = CUSTOMER_CONTROL_TYPES.map((type) => [
    type,
    sorted.filter((h) => (h.customerFacing?.controlTypes || []).includes(type)).length,
    CONTROL_TYPE_COLOR[type],
  ])
  const byModule = {}
  sorted.forEach((h) => {
    const key = h.module || 'Unspecified'
    byModule[key] = (byModule[key] || 0) + 1
  })
  const moduleRows = Object.entries(byModule).sort((a, b) => b[1] - a[1])
  const withPlanned = sorted.filter((h) =>
    (h.controlMeasures || []).some((c) => c?.type === 'planned' && !c?.implemented),
  ).length
  const needsAction = sorted.filter((h) =>
    (h.customerFacing?.controlTypes || []).some((t) => t !== 'Awareness only'),
  ).length

  return (
    <Document
      title={`AmbuNet customer hazard pack - ${now.format('YYYY-MM-DD')}`}
      author="AmbuNet"
      subject="DCB0160 support pack: hazards relevant to deploying organisations"
      options={{ isEvalSupported: false }}
    >
      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image src={AmbuNetLogo} style={{ width: 44, height: 44, marginRight: 10 }} />
            <View>
              <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>
                AmbuNet
              </Text>
              <Text
                style={{ fontSize: 7, color: MUTED, letterSpacing: 1, textTransform: 'uppercase' }}
              >
                Clinical safety
              </Text>
            </View>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 7, color: MUTED }}>
              Issued {now.format('dddd D MMMM YYYY')}
            </Text>
            {downloadedBy ? (
              <Text style={{ fontSize: 7, color: MUTED }}>prepared by {clean(downloadedBy)}</Text>
            ) : null}
          </View>
        </View>

        <View
          style={{
            marginTop: 22,
            paddingVertical: 18,
            paddingHorizontal: 20,
            borderRadius: 6,
            backgroundColor: NAVY,
          }}
        >
          <Text style={{ fontSize: 26, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>
            Customer Hazard Pack
          </Text>
          <Text style={{ fontSize: 9, color: '#CFE8EB', marginTop: 4 }}>
            Hazards in the AmbuNet platform that your organisation needs to know about, to support
            your own DCB0160 clinical safety case
          </Text>
          <Text
            style={{
              fontSize: 7.5,
              color: '#CFE8EB',
              marginTop: 6,
              lineHeight: 1.4,
              maxWidth: 680,
            }}
          >
            AmbuNet manages clinical risk in the product under DCB0129. This pack is the part of
            that work that depends on you: hazards where the control is a setting you choose,
            training you give, a procedure you run, or a limitation you should be aware of. For each
            hazard it states the controls built into the product, the residual risk with those
            controls in place, and what we recommend your organisation does. It is not the full
            manufacturer hazard log, which AmbuNet retains with its evidence and review history.
          </Text>
        </View>

        <SectionTitle>Summary as at {now.format('D MMMM YYYY')}</SectionTitle>
        <View style={{ flexDirection: 'row' }}>
          <StatBox label="Hazards in this pack" value={sorted.length} />
          <StatBox
            label="Needing action by you"
            value={needsAction}
            color={needsAction ? '#B54708' : '#27803B'}
          />
          <StatBox label="Awareness only" value={sorted.length - needsAction} color={TEAL} />
          <StatBox
            label="With a product control still planned"
            value={withPlanned}
            color={withPlanned ? '#5B48B0' : '#27803B'}
          />
          <StatBox
            label="High or critical residual"
            value={residual.counts.Critical + residual.counts.High}
            color={residual.counts.Critical + residual.counts.High ? '#B42318' : '#27803B'}
          />
        </View>

        <View style={{ flexDirection: 'row', marginTop: 14 }}>
          <CountTable title="What you need to do" rows={typeCounts} />
          <CountTable
            title="By residual risk"
            rows={[
              ['Critical', residual.counts.Critical, RISK_COLOR.Critical],
              ['High', residual.counts.High, RISK_COLOR.High],
              ['Medium', residual.counts.Medium, RISK_COLOR.Medium],
              ['Low', residual.counts.Low, RISK_COLOR.Low],
              ['Not scored', residual.other],
            ]}
          />
          <CountTable title="By product area" rows={moduleRows.slice(0, 11)} />
        </View>

        <View style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 8, color: NAVY, fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
            How to use this pack
          </Text>
          <Text style={{ fontSize: 6.5, color: MUTED, lineHeight: 1.4 }}>
            Read each hazard with your clinical safety officer. Where the action is Configuration,
            check the setting in your AmbuNet site configuration. Where it is Training or Procedure,
            decide whether your existing induction and local procedures cover it and record the
            decision in your own hazard log. Where it is Business continuity, make sure your
            downtime procedure covers the situation described. Residual risk ratings are AmbuNet's
            assessment of the product with its controls in place; your own assessment may differ
            with your local controls applied. Report any safety concern or incident involving
            AmbuNet through the in-app "Raise a ticket" option, choosing "Clinical safety incident",
            so it reaches our incident log and safety officer directly.
          </Text>
        </View>

        <Footer />
      </Page>

      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
          fixed
        >
          <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: NAVY }}>
            Customer hazard pack - register
          </Text>
          <Text style={{ fontSize: 7, color: MUTED }}>
            {sorted.length} hazards, issued {now.format('D MMM YYYY')}
          </Text>
        </View>
        <TableHeader />
        {sorted.map((hazard, index) => (
          <RegisterRow
            key={hazard._id || hazard.hazardID}
            hazard={hazard}
            striped={index % 2 === 1}
          />
        ))}
        <Footer />
      </Page>

      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
          fixed
        >
          <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: NAVY }}>
            Customer hazard pack - hazard detail
          </Text>
          <Text style={{ fontSize: 7, color: MUTED }}>
            Impact, product controls, residual risk and recommended action per hazard
          </Text>
        </View>
        {sorted.map((hazard) => (
          <HazardDetail key={hazard._id || hazard.hazardID} hazard={hazard} />
        ))}
        <Footer />
      </Page>
    </Document>
  )
}

export default CustomerHazardPackPDF
